import { QueryKey } from '@/data/query';
import { Button } from '@montugroup/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PrescriptionAssistantTool } from './components/PrescriptionAssistantTool/PrescriptionAssistantTool';
import type { PrescriptionAssistantToolDetails } from './components/PrescriptionAssistantTool/PrescriptionAssistantTool.types';
import { showErrorNotification } from './components/PrescriptionAssistantTool/PrescriptionAssistantTool.utils';
import { PrescriptionAssistantToolActions } from './components/PrescriptionAssistantTool/PrescriptionAssistantToolActions';
import { usePrescriptionAssistant } from './components/PrescriptionAssistantTool/usePrescriptionAssistant';
import { PrescriptionSearch } from './PrescriptionTab/PrescriptionSearch';

interface PrescriptionTabProps {
  isEditable: boolean;
  prescriptionDetails: PrescriptionAssistantToolDetails;
}

enum ActiveView {
  PrescriptionAssistant = 'prescription-assistant',
  SearchPrescriptions = 'search-prescriptions',
}

const PrescriptionTab = ({ isEditable, prescriptionDetails }: PrescriptionTabProps) => {
  const [activeView, setActiveView] = useState<ActiveView>(ActiveView.SearchPrescriptions);
  const queryClient = useQueryClient();

  const {
    primaryButtonLabel,
    isPostingPrescription,
    isLoading,
    showPrescribeStateError,
    setShowPrescribeStateError,
    resetPrescriptionAssistant,
    handleSubmitScript,
  } = usePrescriptionAssistant();

  const closePAT = () => {
    setActiveView(ActiveView.SearchPrescriptions);
    resetPrescriptionAssistant();
  };

  const refetch = async () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GetPatientPrescriptions] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PrescribedProducts] });
  };

  const onCreateScript = async () => {
    if (!prescriptionDetails.patientId) {
      return showErrorNotification();
    }

    const { success } = await handleSubmitScript(prescriptionDetails.patientId, prescriptionDetails.doctorId);
    if (success) {
      await refetch();
      closePAT();
    }
  };

  const handlePrescriptionErrorClosed = async () => {
    setShowPrescribeStateError(false);
    await refetch();
    closePAT();
  };

  const onOnlyCancelComplete = async () => {
    await refetch();
    closePAT();
  };

  return activeView === ActiveView.PrescriptionAssistant ? (
    <>
      <PrescriptionAssistantTool
        isPostingPrescription={isPostingPrescription}
        prescriptionDetails={prescriptionDetails}
        showPrescribeStateError={showPrescribeStateError}
        onPrescriptionErrorClosed={handlePrescriptionErrorClosed}
        onOnlyCancelComplete={onOnlyCancelComplete}
      />
      <PrescriptionAssistantToolActions
        isLoading={isLoading}
        primaryButtonLabel={primaryButtonLabel}
        onCreateScript={onCreateScript}
        extraButtons={
          <Button
            variant="outlined"
            color="secondary"
            onClick={closePAT}
            disabled={isLoading}
            sx={{ borderRadius: 1, textTransform: 'none', letterSpacing: 1, fontWeight: 'bold' }}
          >
            Cancel
          </Button>
        }
      />
    </>
  ) : (
    <PrescriptionSearch
      patientId={Number(prescriptionDetails.patientId)}
      isEditable={isEditable}
      handleCreateScript={() => setActiveView(ActiveView.PrescriptionAssistant)}
    />
  );
};

// eslint-disable-next-line import/no-default-export -- used for lazy loading so requires a default export
export default PrescriptionTab;
